import { LitElement, html, nothing, type PropertyDeclarations } from "lit";
import {
  cssIncludes,
  cssIncludesWithParameter,
  type CssIncludeHost,
} from "../utils/common";

export type ChartStatus = "loading" | "error" | "ready";

export abstract class LmaxBaseChartComponent extends LitElement implements CssIncludeHost {
  static properties: PropertyDeclarations = {
    chartId: { type: String, attribute: "tagid", reflect: true },
    debugMode: { type: Boolean, attribute: "debugmode" },
    overrideCssPath: { type: String, attribute: "overridecsspath" },
    _status: { state: true },
  };

  declare chartId: string;
  declare debugMode: boolean;
  /** Caller-supplied stylesheet path, resolved relative to this instance's own script path — see `cssIncludesResolved`. */
  declare overrideCssPath: string;
  protected declare _status: ChartStatus;

  protected cleanupChart?: () => void;

  /** Per-instance cache of `getScriptPath()`'s result — see `CssIncludeHost`. */
  cachedScriptPath = "";

  constructor() {
    super();
    this.chartId = "";
    this.debugMode = false;
    this.overrideCssPath = "";
    this._status = "loading";
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.debugLog("disconnectedCallback", { hadChart: !!this.cleanupChart });
    this.cleanupChart?.();
    this.cleanupChart = undefined;
  }

  protected readCssVar(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim();
  }

  /**
   * No-op unless `debugmode` is set on this instance. Every subclass
   * (latency chart, spread chart, this panel wrapper) calls this at each
   * step of its own data-flow — connect, fetch start/success/error, rebuild,
   * teardown — so setting `debugmode` on one chart traces just that
   * instance's own sequence in the console, tagged with its tag name and
   * `tagId` so multiple charts on one page don't interleave unreadably.
   */
  protected debugLog(stage: string, details?: Record<string, unknown>): void {
    if (!this.debugMode) return;
    const label = `[${this.tagName.toLowerCase()}${this.chartId ? `#${this.chartId}` : ""}] ${stage}`;
    if (details) console.log(label, details);
    else console.log(label);
  }

  /** Flags this instance's stylesheet(s) as settled — loaded or errored. See `cssIncludes`/`cssIncludesWithParameter`. */
  _markCssReady(): void {
    this.setAttribute("css-ready", "");
  }

  /** Shared `base.css`, resolved relative to this instance's own script path. */
  protected get cssIncludes() {
    return cssIncludes(this);
  }

  /** `cssIncludes`, plus a caller-supplied stylesheet resolved the same way — see `common.ts`. */
  protected cssIncludesWithParameter(customPath: string) {
    return cssIncludesWithParameter(this, customPath);
  }

  /** `cssIncludes`, overridden with `overrideCssPath` when set — the CSS include a chart's `render()` should use. */
  protected get cssIncludesResolved() {
    this.debugLog("cssIncludesResolved", { overrideCssPath: this.overrideCssPath || "(none)" });
    return this.overrideCssPath ? this.cssIncludesWithParameter(this.overrideCssPath) : this.cssIncludes;
  }

  /** The loading/error status messages every chart shows identically; renders nothing once `_status` is `"ready"`. */
  protected renderStatus(loadingText: string, errorText: string) {
    return html`
      ${this._status === "loading" ? html`<p class="status-message">${loadingText}</p>` : nothing}
      ${this._status === "error"
        ? html`<p class="status-message status-message--error">${errorText}</p>`
        : nothing}
    `;
  }
}
